import { useState } from 'react';
import type { FormEvent } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/daihb0roh653ivfvodd0';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const typeOptions = ['提供线索', '内容更正', '合作咨询', '其他事项'];

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');
  const [content, setContent] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const trap = String(formData.get('contact_alt') ?? '').trim();
    if (trap) {
      setStatus('success');
      setMessage('已收到你的投稿，编辑会尽快查看。');
      form.reset();
      setContent('');
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'contact_alt') return;
      const text = typeof value === 'string' ? value : '';
      if (text.trim() === '') return;
      payload.append(key, text);
    });

    setStatus('submitting');
    setMessage('');

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const code = parsed?.code as string | undefined;
      const serverMsg = String(meta.message || meta.detail || parsed?.message || responseText || '');
      const lower = serverMsg.toLowerCase();
      const isSpam = lower.includes('spam') || serverMsg.includes('垃圾');

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        setMessage('已收到你的投稿，编辑会尽快查看。');
        form.reset();
        setContent('');
      } else {
        setStatus('error');
        setMessage(serverMsg.trim() ? serverMsg : '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，提交未成功，请稍后重试。');
    }
  };

  return (
    <form
      data-readdy-form
      id="submit-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="nickname" className="mb-2 block text-sm font-label text-foreground-700">
            昵称
          </label>
          <input
            id="nickname"
            name="nickname"
            type="text"
            required
            autoComplete="nickname"
            placeholder="怎么称呼你"
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="email" className="mb-2 block text-sm font-label text-foreground-700">
            联系邮箱
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="用于编辑回复，不会公开"
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="type" className="mb-2 block text-sm font-label text-foreground-700">
          投稿类型
        </label>
        <select
          id="type"
          name="type"
          defaultValue={typeOptions[0]}
          className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400"
        >
          {typeOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-5">
        <label htmlFor="content" className="mb-2 block text-sm font-label text-foreground-700">
          内容说明
        </label>
        <textarea
          id="content"
          name="content"
          required
          maxLength={500}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={5}
          placeholder="请说明信息来源与可公开的部分，建议附上公开链接（不超过 500 字）"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-3 text-sm leading-relaxed text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
        />
        <p className="mt-2 text-right text-xs text-foreground-500">{content.length} / 500</p>
      </div>

      <input
        className="ref-code-check"
        type="text"
        name="contact_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-foreground-500">
          提交后编辑会在值守时间内查看，我们不会公开你的联系方式。
        </p>
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-fill'} />
          {status === 'submitting' ? '提交中…' : '提交投稿'}
        </button>
      </div>

      {status === 'success' ? (
        <p
          role="status"
          className="mt-5 flex items-center gap-2 rounded-md border border-secondary-300/50 bg-secondary-100/70 px-4 py-3 text-sm text-secondary-400"
        >
          <i className="ri-checkbox-circle-line text-base" />
          {message}
        </p>
      ) : null}

      {status === 'error' ? (
        <p
          role="alert"
          className="mt-5 flex items-center gap-2 rounded-md border border-primary-400/50 bg-primary-100/60 px-4 py-3 text-sm text-primary-300"
        >
          <i className="ri-error-warning-line text-base" />
          {message}
        </p>
      ) : null}
    </form>
  );
}